import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  useMediaQuery,
  Box,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { UUID } from 'crypto'

import {
  ProfileRegeneratingError,
  isProfileRegeneratingError,
} from '@/api/profileRegenError'
import { triggerProfileRegen } from '@/api/orgsAPI'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useCurrentUserOrgs, useProfileRegenPoll } from '@/utils/hooks/orgs'
import { deriveBlockPhase } from '@/utils/profileRegenPhase'

interface ProfileRegenBlockContextValue {
  // Show the block dialog for a regen 409. Returns nothing.
  report: (error: ProfileRegeneratingError) => void
  // True while the dialog is open, so age-based notices (e.g. the stale-scan
  // dialog) can step aside and let the block message win.
  isBlocked: boolean
}

const ProfileRegenBlockContext =
  createContext<ProfileRegenBlockContextValue | null>(null)

export function useProfileRegenBlock(): ProfileRegenBlockContextValue {
  const ctx = useContext(ProfileRegenBlockContext)
  if (!ctx) {
    throw new Error(
      'useProfileRegenBlock must be used within a ProfileRegenBlockProvider',
    )
  }
  return ctx
}

// Convenience for catch/onError sites: if `error` is the profile-regen 409, show
// the shared dialog and return true (handled); otherwise return false so the
// caller runs its normal error handling.
export function useProfileRegenGuard(): (error: unknown) => boolean {
  const { report } = useProfileRegenBlock()
  return useCallback(
    (error: unknown) => {
      if (isProfileRegeneratingError(error)) {
        report(error)
        return true
      }
      return false
    },
    [report],
  )
}

const FUNDING_PROFILE_PATH = '/profile/funding-profile'

export default function ProfileRegenBlockProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [blockError, setBlockError] = useState<ProfileRegeneratingError | null>(
    null,
  )
  // The retry we kick off threw (couldn't even start).
  const [retryError, setRetryError] = useState(false)
  // We've refetched the org status since this open, so `liveStatus` is fresh (not a
  // cached 'idle' from before another tab/user started the regen). "Done" waits on it.
  const [confirmedFresh, setConfirmedFresh] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // The indeterminate bar animates continuously; MUI doesn't honour the OS setting,
  // so drop the animated bar for reduced-motion users — the text carries the state.
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  const { user, token, orgs } = getCurrentUser()
  const { orgData } = useCurrentUserOrgs(token as string, user, orgs as UUID[])
  const orgUuid = user?.selected_org_uuid ?? null
  // Live regen status for the current org — this drives the dialog once it's open.
  const liveStatus = orgData.find(
    (o) => o.uuid === orgUuid,
  )?.profile_regen_status
  // Poll just this org while it's updating so the dialog progresses to "done".
  useProfileRegenPoll(
    user,
    orgUuid ?? undefined,
    liveStatus === 'in_progress' || liveStatus === 'pending',
  )

  const isOpen = blockError !== null
  const isFailed = blockError?.state === 'failed'
  const needsInfo = isFailed && blockError?.reason === 'insufficient_data'

  // Fired the retry for this open; and whether that retry has been kicked off and
  // its status refetched — so a failed retry is recognised even when the poll never
  // catches its brief in_progress.
  const retryStartedRef = useRef(false)
  const [retrySettled, setRetrySettled] = useState(false)

  const report = useCallback(
    async (error: ProfileRegeneratingError) => {
      // Refetch so the phase + poll use the real status, not a cached 'idle' from
      // before another tab/user started the regen (which would open the dialog at
      // "up to date" and never poll). The 'done' phase waits on this fresh read.
      setConfirmedFresh(false)
      setBlockError(error)
      try {
        await queryClient.refetchQueries({
          queryKey: ['currentOrgs', user?.uuid],
        })
      } finally {
        setConfirmedFresh(true)
      }
    },
    [queryClient, user?.uuid],
  )

  const close = useCallback(() => setBlockError(null), [])

  const startRetry = useCallback(async () => {
    if (!token || !orgUuid) return
    setRetryError(false)
    setRetrySettled(false)
    try {
      await triggerProfileRegen(token as string, orgUuid)
      // Refetch so the status flips to in_progress and the poll takes over.
      await queryClient.invalidateQueries({
        queryKey: ['currentOrgs', user?.uuid],
      })
      // Retry kicked off + status refetched: a 'failed' status from here on is a
      // failed retry (even if it failed too fast for the poll to see in_progress).
      setRetrySettled(true)
    } catch (err) {
      if (isProfileRegeneratingError(err)) {
        // A regen is already running (e.g. another tab/user started it): that's what
        // the retry wanted — refetch and let the poll drive this dialog to 'done'
        // rather than showing a retry failure.
        await queryClient.invalidateQueries({
          queryKey: ['currentOrgs', user?.uuid],
        })
        setRetrySettled(true)
        return
      }
      setRetryError(true)
    }
  }, [token, orgUuid, queryClient, user?.uuid])

  // On close, reset the per-open guards.
  useEffect(() => {
    if (isOpen) return
    retryStartedRef.current = false
    setRetrySettled(false)
    setRetryError(false)
    setConfirmedFresh(false)
  }, [isOpen])

  // Auto-fire the retry once when a transient (error) failure is reported —
  // insufficient-data needs the wizard, and in-progress is already running.
  useEffect(() => {
    if (!isOpen || !isFailed || needsInfo || retryStartedRef.current) return
    retryStartedRef.current = true
    startRetry()
  }, [isOpen, isFailed, needsInfo, startRetry])

  // Phase drives the copy + actions. Insufficient-data is its own branch; the rest
  // is the updating→done (or failed-again) lifecycle.
  const phase = deriveBlockPhase({
    needsInfo,
    retryError,
    liveStatus,
    retrySettled,
    confirmedFresh,
  })

  // Backdrop/Escape only dismiss the resolved end-states. During 'updating' a stray
  // click shouldn't close it (they can still close deliberately via the button), and
  // 'needsinfo' must be answered.
  const allowBackdropClose = phase === 'done' || phase === 'failedagain'

  const goToProfile = (recover = false) => {
    close()
    navigate(
      FUNDING_PROFILE_PATH,
      recover ? { state: { recoverProfile: true } } : {},
    )
  }

  const title =
    phase === 'needsinfo'
      ? 'A few more details needed'
      : phase === 'failedagain'
        ? "We couldn't update your profile"
        : phase === 'done'
          ? 'Your profile is up to date!'
          : "We're updating your profile"

  return (
    <ProfileRegenBlockContext.Provider value={{ report, isBlocked: isOpen }}>
      {children}
      <Dialog
        open={isOpen}
        onClose={allowBackdropClose ? close : undefined}
        disableEscapeKeyDown={!allowBackdropClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {phase === 'needsinfo' && (
            <Typography sx={{ mt: 1 }}>{blockError?.message}</Typography>
          )}
          {phase === 'updating' && (
            <>
              <Typography sx={{ mt: 1, mb: 2 }}>
                We're updating your profile with your latest information.
              </Typography>
              <Typography sx={{ mt: 1, mb: 2 }}>
                This can take a moment — you can review your profile once it's
                done, or close this message and it will update in the
                background.
              </Typography>
              {!prefersReducedMotion && (
                <LinearProgress
                  sx={{
                    borderRadius: 1,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': { backgroundColor: '#1C3045' },
                  }}
                />
              )}
            </>
          )}
          {phase === 'done' && (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ mt: 1 }}>
                Your profile has finished updating with your latest information.
              </Typography>
              <Typography sx={{ mt: 1 }}>
                Review your updated profile, or close this message and continue
                with what you were doing.
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  mt: 2,
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  fontSize: '14px',
                }}
              >
                You can always review your profile at any time by going to{' '}
                <span style={{ fontWeight: 600 }}>Manage Organisation</span>,
                and selecting the{' '}
                <span style={{ fontWeight: 600 }}>Funding Profile</span> tab.
              </Typography>
            </Box>
          )}
          {phase === 'failedagain' && (
            <Typography sx={{ mt: 1 }}>
              Something went wrong while updating your profile. You can try
              again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {phase === 'needsinfo' && (
            <Button
              variant="contained"
              disableRipple
              onClick={() => goToProfile(true)}
              sx={{
                bgcolor: '#1C3045',
                textTransform: 'none',
                fontSize: '16px',
              }}
            >
              Answer questions
            </Button>
          )}
          {phase === 'updating' && (
            <Button onClick={close} disableRipple>
              Close
            </Button>
          )}
          {phase === 'done' && (
            <>
              <Button
                variant="contained"
                disableRipple
                onClick={() => goToProfile(false)}
                sx={{
                  bgcolor: '#1C3045',
                  textTransform: 'none',
                  fontSize: '16px',
                }}
              >
                Review profile
              </Button>
              <Button
                variant="outlined"
                disableRipple
                onClick={close}
                sx={{
                  color: '#1C3045',
                  borderColor: '#1C3045',
                  textTransform: 'none',
                  fontSize: '16px',
                }}
              >
                Close
              </Button>
            </>
          )}
          {phase === 'failedagain' && (
            <>
              <Button onClick={close} disableRipple>
                Close
              </Button>
              <Button
                variant="contained"
                disableRipple
                onClick={() => {
                  retryStartedRef.current = true
                  startRetry()
                }}
                sx={{
                  bgcolor: '#1C3045',
                  textTransform: 'none',
                  fontSize: '16px',
                }}
              >
                Try again
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </ProfileRegenBlockContext.Provider>
  )
}
